import React from 'react';

export default function MonthlySection({
  calendarCurrentDate,
  moveCalendarMonth,
  renderCalendarDays,
  achievedRewards,
  monthlyWorkouts,
  monthlyCardioWorkouts,
  expandedMonthly,
  toggleMonthlyExpand,
  captureReportToImage
}) {
  return (
    <div className="animate-fadeIn space-y-6">
      <div id="print-header" className="hidden">
        <h2 className="text-2xl font-black text-slate-900">WORKOUT REPORT</h2>
        <p id="print-period" className="text-sm font-bold text-slate-500 mt-1">Period: {calendarCurrentDate.getFullYear()}년 {calendarCurrentDate.getMonth() + 1}월</p>
      </div>

      <div id="captureArea" className="space-y-6 bg-transparent">
        <div id="reportCalendarCard" className="card p-5 bg-white rounded-3xl shadow-sm border border-slate-100">
          <div className="flex justify-between items-center mb-4">
            <h3 id="calendarMonthTitle" className="font-extrabold text-slate-800 text-base">
              {calendarCurrentDate.getFullYear()}년 {calendarCurrentDate.getMonth() + 1}월
            </h3>
            <div className="flex gap-1 print-hide">
              <button onClick={() => moveCalendarMonth(-1)} className="p-1 text-slate-500 hover:bg-slate-100 rounded-lg">
                <span className="material-icons-round text-sm">chevron_left</span>
              </button>
              <button onClick={() => moveCalendarMonth(1)} className="p-1 text-slate-500 hover:bg-slate-100 rounded-lg">
                <span className="material-icons-round text-sm">chevron_right</span>
              </button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-y-2 text-center text-xs font-semibold text-slate-400 mb-2">
            <div className="text-rose-500">일</div><div>월</div><div>화</div><div>수</div><div>목</div><div>금</div><div className="text-blue-500">토</div>
          </div>
          <div id="calendarDaysGrid" className="grid grid-cols-7 gap-y-3 text-center text-sm font-bold text-slate-700">
            {renderCalendarDays()}
          </div>
        </div>

        <div className="card p-5 bg-white rounded-3xl shadow-sm border border-slate-100 space-y-3">
          <div className="flex items-center gap-2 px-1">
            <span className="material-icons-round text-amber-500 text-base">emoji_events</span>
            <h4 className="text-sm font-black text-slate-800">획득한 명예 리워드</h4>
          </div>
          
          {achievedRewards.length === 0 ? (
            <p className="text-xs font-medium text-slate-400 text-center py-4">아직 달성한 리워드가 없습니다.</p>
          ) : (
            <div className="grid grid-cols-4 gap-3 pt-2">
              {achievedRewards.map((reward) => (
                <div key={reward.id} className="flex flex-col items-center text-center space-y-1">
                  <div className="w-12 h-12 rounded-xl overflow-hidden border border-slate-100 shadow-sm bg-slate-50">
                    <img 
                      src={reward.img} 
                      alt={reward.title} 
                      className="w-full h-full object-cover"
                      onError={(e) => e.target.src = '/images/placeholder.png'}
                      crossOrigin="anonymous" 
                    />
                  </div>
                  <span className="text-[9px] font-black text-slate-700 truncate w-full px-0.5">{reward.title}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-8 space-y-6">
          <h3 className="text-lg font-black text-slate-800 mb-2 px-1">종목별 월간 요약</h3>
          
          {monthlyWorkouts.length === 0 && monthlyCardioWorkouts.length === 0 ? (
            <div className="p-12 text-center flex flex-col items-center justify-center bg-white rounded-3xl border border-slate-200 shadow-sm">
              <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mb-4">
                <span className="material-icons-round text-2xl text-slate-300">event_busy</span>
              </div>
              <p className="text-sm font-black text-slate-600">이달의 운동 기록이 없습니다.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {monthlyWorkouts.map((workout, index) => {
                const isExpanded = expandedMonthly[workout.name];
                const visibleHistory = isExpanded ? workout.history : workout.history.slice(0, 3);
                return (
                  <div key={index} className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden animate-slideUp">
                    <div className="p-6 bg-slate-50/50 border-b border-slate-100 flex items-center justify-between">
                      <div>
                        <h4 className="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
                          <span className="material-icons-round text-blue-500 text-lg">fitness_center</span>
                          {workout.name}
                        </h4>
                        <p className="text-[11px] font-bold text-slate-400 mt-1 uppercase">Monthly Best (무산소)</p>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-black text-blue-600 tracking-tighter">
                          <span className="text-sm font-bold text-slate-500 mr-2">최고</span>
                          {workout.absoluteMaxWeight}<span className="text-sm font-bold text-slate-500 ml-1">kg</span>
                        </div>
                        <div className="text-xs font-bold text-slate-500 mt-0.5">× {workout.absoluteMaxReps}회</div>
                      </div>
                    </div>
                    <div className="p-5">
                      <h5 className="text-[10px] font-black text-slate-400 mb-3 px-1 uppercase flex justify-between items-center">
                        <span>History</span>
                        <span className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">총 {workout.history.length}회</span>
                      </h5>
                      <div className="space-y-2">
                        {visibleHistory.map((hist, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 rounded-2xl bg-white border border-slate-100">
                            <div className="flex items-center gap-3">
                              <div className="w-2 h-2 rounded-full bg-slate-300"></div>
                              <span className="text-sm font-bold text-slate-600">{hist.date.slice(5).replace('-', '.')}</span>
                              <span className="text-[10px] font-bold text-blue-500 bg-blue-50 px-2 py-0.5 rounded-md">{hist.setsCount} Sets</span>
                            </div>
                            <div className="text-xs font-black text-slate-700">{hist.maxWeight}kg <span className="text-[10px] font-medium text-slate-400">({hist.maxReps}회)</span></div>
                          </div>
                        ))}
                      </div>
                      {workout.history.length > 3 && (
                        <button onClick={() => toggleMonthlyExpand(workout.name)} className="w-full mt-3 py-2.5 text-[11px] font-black text-slate-500 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors flex justify-center items-center gap-1">
                          {isExpanded ? <>접기 <span className="material-icons-round text-sm">expand_less</span></> : <>+ {workout.history.length - 3}개의 이전 기록 더보기 <span className="material-icons-round text-sm">expand_more</span></>}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {monthlyCardioWorkouts.map((workout, index) => {
                const isExpanded = expandedMonthly[workout.name];
                const visibleHistory = isExpanded ? workout.history : workout.history.slice(0, 3);
                return (
                  <div key={index} className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden animate-slideUp">
                    <div className="p-6 bg-slate-50/50 border-b border-slate-100 flex items-center justify-between">
                      <div>
                        <h4 className="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
                          <span className="material-icons-round text-teal-500 text-lg">directions_run</span>
                          {workout.name}
                        </h4>
                        <p className="text-[11px] font-bold text-slate-400 mt-1 uppercase">Monthly Best (유산소)</p>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-black text-teal-600 tracking-tighter">
                          <span className="text-xl">
                            {workout.maxTotalTime > 0 ? `${workout.maxTotalTime}분 ` : ''}
                            {workout.maxTotalDistance > 0 ? `${workout.maxTotalDistance}km` : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="p-5">
                      <h5 className="text-[10px] font-black text-slate-400 mb-3 px-1 uppercase flex justify-between items-center">
                        <span>History</span>
                        <span className="bg-teal-50 text-teal-600 px-2 py-0.5 rounded-full">총 {workout.history.length}회</span>
                      </h5>
                      <div className="space-y-2">
                        {visibleHistory.map((hist, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 rounded-2xl bg-white border border-slate-100">
                            <div className="flex items-center gap-3">
                              <div className="w-2 h-2 rounded-full bg-slate-300"></div>
                              <span className="text-sm font-bold text-slate-600">{hist.date.slice(5).replace('-', '.')}</span>
                              <span className="text-[10px] font-bold text-teal-500 bg-teal-50 px-2 py-0.5 rounded-md">{hist.setsCount} Sets</span>
                            </div>
                            <div className="text-xs font-black text-slate-700">
                              {hist.totalTime > 0 ? `${hist.totalTime}분 ` : ''}
                              {hist.totalDistance > 0 ? `${hist.totalDistance}km` : ''}
                            </div>
                          </div>
                        ))}
                      </div>
                      {workout.history.length > 3 && (
                        <button onClick={() => toggleMonthlyExpand(workout.name)} className="w-full mt-3 py-2.5 text-[11px] font-black text-slate-500 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors flex justify-center items-center gap-1">
                          {isExpanded ? <>접기 <span className="material-icons-round text-sm">expand_less</span></> : <>+ {workout.history.length - 3}개의 이전 기록 더보기 <span className="material-icons-round text-sm">expand_more</span></>}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <button id="captureBtn" onClick={captureReportToImage} className="w-full mt-8 h-16 bg-blue-600 text-white rounded-2xl font-bold flex items-center justify-center gap-2 shadow-xl shadow-blue-100 hover:bg-blue-700 transition-colors">
          <span className="material-icons-round text-xl">share</span>
          성장 리포트 이미지 저장
        </button>
      </div>
    </div>
  );
}